import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/i18n/I18nContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MessageSquare, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error(t('auth.enterEmail'));
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
      toast.success(t('auth.resetLinkSent'));
    } catch (error) {
      toast.error(t('auth.sendError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center space-x-2 mb-8">
          <MessageSquare className="w-10 h-10 text-emerald-600" />
          <span className="text-3xl font-bold text-slate-900">EVA AI</span>
        </div>

        <Card data-testid="forgot-password-form">
          <CardHeader className="text-center">
            <div className="flex justify-end mb-2">
              <LanguageSelector />
            </div>
            <CardTitle className="text-2xl">{t('auth.forgotPasswordTitle')}</CardTitle>
            <CardDescription>
              {t('auth.forgotPasswordDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <Alert className="border-emerald-200 bg-emerald-50">
                <AlertDescription className="text-emerald-800">
                  {t('auth.resetLinkSentLong')}
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="nome@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="input-email"
                    disabled={loading}
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={loading}
                  data-testid="button-submit"
                >
                  {loading ? t('auth.sending') : t('auth.sendResetLink')}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center text-sm text-slate-600">
              <a
                href="/login"
                className="text-emerald-600 hover:underline flex items-center justify-center gap-2"
                data-testid="link-back-to-login"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('auth.backToLogin')}
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
